<script setup>
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import Swal from "sweetalert2";
import api from "../../api/axios";
import DataTable from "vue3-easy-data-table";
import "vue3-easy-data-table/dist/style.css";

import ArgonButton from "@/components/ArgonButton.vue";
import ArgonInput from "@/components/ArgonInput.vue";
import { useAuth } from "@/composables/useAuth";

const router = useRouter();
const { user } = useAuth();
const isAdmin = computed(() => user.value?.role === "admin");

// =======================
// STATE
// =======================
const contacts = ref([]);
const asesores = ref([]);
const crmCompanies = ref([]);

const search = ref("");
const companyFilter = ref("");

const showModal = ref(false);
const isEdit = ref(false);
const guardando = ref(false);

const form = ref({
  id: null,
  name: "",
  lastName: "",
  email: "",
  phone: "",
  source: "manual",
  crmCompanyId: "",
  ownerUserId: ""
});

// =======================
// TABLE HEADERS
// =======================
const headers = [
  { text: "Nombre", value: "name" },
  { text: "Teléfono", value: "phone" },
  { text: "Email", value: "email" },
  { text: "Empresa", value: "crm_company_name" },
  { text: "Asesor", value: "owner_name" },
  { text: "Acciones", value: "actions", sortable: false }
];

// =======================
// FILTROS (client-side)
// =======================
const filteredContacts = computed(() => {
  return contacts.value.filter(c => {
    const matchSearch =
      (c.name || "").toLowerCase().includes(search.value.toLowerCase()) ||
      (c.phone || "").toLowerCase().includes(search.value.toLowerCase()) ||
      (c.email || "").toLowerCase().includes(search.value.toLowerCase());

    const matchCompany = companyFilter.value
      ? c.crm_company_id === Number(companyFilter.value)
      : true;

    return matchSearch && matchCompany;
  });
});

// =======================
// API
// =======================
const getContacts = async () => {
  const res = await api.get("/crm/contacts", { params: { limit: 500 } });

  const conNombreAsesor = res.data.contacts.map(c => ({
    ...c,
    owner_name: asesores.value.find(a => a.id === c.owner_user_id)?.name || "—"
  }));

  contacts.value = conNombreAsesor;
};

const getAsesores = async () => {
  if (!isAdmin.value) return;

  const res = await api.get("/users/list");
  asesores.value = res.data.data;
};

const getCrmCompanies = async () => {
  const res = await api.get("/crm/companies", { params: { limit: 200 } });
  crmCompanies.value = res.data.companies;
};

const saveContact = async () => {
  guardando.value = true;

  try {
    if (isEdit.value) {
      await api.put(`/crm/contacts/${form.value.id}`, form.value);
    } else {
      await api.post("/crm/contacts", form.value);
    }

    showModal.value = false;
    resetForm();
    await getContacts();

    Swal.fire({
      icon: "success",
      title: isEdit.value ? "Contacto actualizado" : "Contacto creado",
      timer: 2000,
      showConfirmButton: false
    });

  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudo guardar el contacto",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    });
  } finally {
    guardando.value = false;
  }
};

const editContact = (contact) => {
  form.value = {
    id: contact.id,
    name: contact.name,
    lastName: contact.last_name || "",
    email: contact.email || "",
    phone: contact.phone || "",
    source: contact.source || "manual",
    crmCompanyId: contact.crm_company_id || "",
    ownerUserId: contact.owner_user_id || ""
  };
  isEdit.value = true;
  showModal.value = true;
};

const deleteContact = async (id) => {
  const { isConfirmed } = await Swal.fire({
    icon: "warning",
    title: "¿Eliminar contacto?",
    text: "Esta acción no se puede deshacer.",
    showCancelButton: true,
    confirmButtonText: "Sí, eliminar",
    cancelButtonText: "Cancelar",
    confirmButtonColor: "#f5365c"
  });

  if (!isConfirmed) return;

  try {
    await api.delete(`/crm/contacts/${id}`);
    await getContacts();
  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudo eliminar el contacto",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    });
  }
};

const verContacto = (id) => {
  router.push(`/crm/contacts/${id}`);
};

const openCreate = () => {
  resetForm();
  isEdit.value = false;
  showModal.value = true;
};

const resetForm = () => {
  form.value = {
    id: null,
    name: "",
    lastName: "",
    email: "",
    phone: "",
    source: "manual",
    crmCompanyId: "",
    ownerUserId: ""
  };
};

// =======================
onMounted(async () => {
  await Promise.all([getAsesores(), getCrmCompanies()]);
  await getContacts();
});
</script>

<template>
  <div class="container-fluid py-4">

    <!-- HEADER -->
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h4 class="mb-0 crm-header-title">
        <i class="bi bi-person-lines-fill me-2"></i>
        Contactos (CRM)
      </h4>

      <div class="d-flex gap-2">
        <router-link to="/crm/pipeline" class="btn btn-outline-secondary crm-embudo-btn mb-0">
          <i class="bi bi-kanban me-1"></i>
          Ver embudo
        </router-link>

        <argon-button color="success" @click="openCreate">
          <i class="bi bi-plus-circle me-1"></i>
          Nuevo contacto
        </argon-button>
      </div>
    </div>

    <!-- FILTROS -->
    <div class="card mb-3">
      <div class="card-body row g-3">

        <div class="col-md-6">
          <label>Buscar</label>
          <argon-input v-model="search" placeholder="Nombre, teléfono o email..." />
        </div>

        <div class="col-md-3">
          <label>Empresa</label>
          <select v-model="companyFilter" class="form-control">
            <option value="">Todas</option>
            <option v-for="c in crmCompanies" :key="c.id" :value="c.id">{{ c.name }}</option>
          </select>
        </div>

      </div>
    </div>

    <!-- TABLA -->
    <div class="card">
      <div class="card-body">

        <DataTable
          :headers="headers"
          :items="filteredContacts"
          :rows-per-page="10"
          :rows-items="[5, 10, 20, 50]"
          table-class-name="table table-striped"

          :pagination-options="{
            rowsPerPageText: 'Filas por página',
            rangeSeparatorText: 'de',
            selectLabel: 'Filas',
            selectSuffix: '',
            paginationLabel: ({ start, end, total }) => `${start}-${end} de ${total}`
          }"

          :table-locale="{
            noDataAvailable: 'No hay datos disponibles',
            noResults: 'No se encontraron resultados'
          }"
        >

          <!-- Nombre (clic para ver detalle) -->
          <template #item-name="{ id, name, last_name }">
            <a href="#" class="text-primary" @click.prevent="verContacto(id)">
              {{ name }} {{ last_name || "" }}
            </a>
          </template>

          <!-- Empresa -->
          <template #item-crm_company_name="{ crm_company_name }">
            {{ crm_company_name || "—" }}
          </template>

          <!-- Acciones -->
          <template #item-actions="{ id, ...contact }">
            <button class="btn btn-info btn-sm me-2" @click="verContacto(id)">
              <i class="bi bi-eye"></i>
            </button>

            <button class="btn btn-warning btn-sm me-2" @click="editContact({ id, ...contact })">
              <i class="bi bi-pencil"></i>
            </button>

            <button class="btn btn-danger btn-sm" @click="deleteContact(id)">
              <i class="bi bi-trash"></i>
            </button>
          </template>

        </DataTable>

      </div>
    </div>

    <!-- MODAL -->
    <div v-if="showModal" class="modal fade show d-block">
      <div class="modal-dialog">
        <div class="modal-content">

          <div class="modal-header">
            <h5>
              <i class="bi" :class="isEdit ? 'bi-pencil' : 'bi-person-plus'"></i>
              {{ isEdit ? " Editar contacto" : " Nuevo contacto" }}
            </h5>
            <button class="btn-close" @click="showModal = false"></button>
          </div>

          <div class="modal-body">

            <div class="row g-3">
              <div class="col-md-6">
                <label>Nombre</label>
                <argon-input v-model="form.name" />
              </div>

              <div class="col-md-6">
                <label>Apellido</label>
                <argon-input v-model="form.lastName" />
              </div>
            </div>

            <div class="mb-3 mt-3">
              <label>Teléfono</label>
              <argon-input v-model="form.phone" />
            </div>

            <div class="mb-3">
              <label>Email</label>
              <argon-input v-model="form.email" />
            </div>

            <div class="mb-3">
              <label>Origen</label>
              <select v-model="form.source" class="form-control">
                <option value="manual">Manual</option>
                <option value="whatsapp">WhatsApp</option>
                <option value="web">Web</option>
              </select>
            </div>

            <div class="mb-3">
              <label>Empresa</label>
              <select v-model="form.crmCompanyId" class="form-control">
                <option value="">Sin empresa</option>
                <option v-for="c in crmCompanies" :key="c.id" :value="c.id">{{ c.name }}</option>
              </select>
            </div>

            <div v-if="isAdmin" class="mb-3">
              <label>Asesor asignado</label>
              <select v-model="form.ownerUserId" class="form-control">
                <option value="">Sin asignar</option>
                <option v-for="a in asesores" :key="a.id" :value="a.id">
                  {{ a.name }}
                </option>
              </select>
            </div>

          </div>

          <div class="modal-footer">
            <button class="btn btn-secondary" @click="showModal = false">
              Cancelar
            </button>

            <button class="btn btn-success" :disabled="guardando" @click="saveContact">
              <i class="bi bi-save me-1"></i>
              Guardar
            </button>
          </div>

        </div>
      </div>
    </div>

    <div v-if="showModal" class="modal-backdrop fade show"></div>

  </div>
</template>

<style scoped>
.crm-header-title,
.crm-embudo-btn {
  color: #fff !important;
  border-color: #fff !important;
}
</style>
